import { styled } from '@sds-system/system';
import { theme } from '@sds-system/theme';
import { getAllStyledProps } from '@sds-system/utils';
import { Column } from '../Column';
import { Row } from '../Row';
import type {
  ComboBoxSizeKey,
  StyledComboBoxContentProps,
  StyledComboBoxRootProps,
  StyledComboBoxTriggerProps,
  StyledComboBoxWrapperProps,
} from './ComboBox.types';

const comboBoxPaddingStyle = (size?: ComboBoxSizeKey, isContent?: boolean) => {
  switch (size) {
    case 'M':
      return {
        padding: isContent ? '12px 16px' : '8px 16px',
        height: isContent ? theme.size.L : theme.size.M,
        ...theme.typography.body2.regular,
      };
    case 'L':
      return {
        padding: '12px 16px',
        height: theme.size.L,
        ...theme.typography.body2.regular,
      };
    case 'XL':
      return {
        padding: '16px',
        height: theme.size.XL,
        ...theme.typography.body.regular,
      };
    default:
      return {
        padding: '12px 16px',
        height: theme.size.L,
        ...theme.typography.body2.regular,
      };
  }
};

export const ComboBoxRootColumn = styled(Column, {
  componentName: 'ComboBox',
  slot: 'root',
})<StyledComboBoxRootProps>(() => {
  return {};
}, getAllStyledProps);

export const ComboBoxContainer = styled(Row, {
  componentName: 'ComboBox',
  slot: 'trigger',
})<StyledComboBoxTriggerProps>(({ theme, size = 'L', width, errorMessage, disabled }) => {
  if (disabled) {
    return {
      position: 'relative',
      backgroundColor: theme.palette.bg.disabledLight,
      border: '1px solid',
      borderColor: errorMessage ? theme.palette.border.error : theme.palette.border.disabled,
      color: theme.palette.text.disabled,
      borderRadius: theme.borderRadius[8],
      width: width,
      cursor: 'not-allowed',
      ...comboBoxPaddingStyle(size),
    };
  }

  return {
    position: 'relative',
    backgroundColor: theme.palette.bg.primary,
    border: '1px solid',
    borderColor: errorMessage ? theme.palette.border.error : theme.palette.border.secondary,
    borderRadius: theme.borderRadius[8],
    width: width,
    cursor: 'pointer',
    ...comboBoxPaddingStyle(size),
    ...(errorMessage && { pointerEvents: 'none' }),
  };
}, getAllStyledProps);

export const DropdownWrapper = styled(Column, {
  componentName: 'ComboBox',
  slot: 'wrapper',
})<StyledComboBoxWrapperProps>(({ theme }) => {
  return {
    width: 'inherit',
    position: 'fixed',
    backgroundColor: theme.palette.bg.primary,
    border: '1px solid',
    borderColor: theme.palette.border.secondary,
    borderRadius: theme.borderRadius[8],
    zIndex: theme.zIndex[1000],
    maxHeight: 400,
    overflow: 'auto',
  };
}, getAllStyledProps);

export const DropdownContent = styled(Row, {
  componentName: 'ComboBox',
  slot: 'content',
})<StyledComboBoxContentProps>(({ theme, size = 'L', checked }) => {
  return {
    ...comboBoxPaddingStyle(size, true),
    ...(checked && {
      backgroundColor: theme.palette.bg.interactive.selected,
    }),
    '&:first-of-type': {
      borderRadius: `${theme.borderRadius[8]}px ${theme.borderRadius[8]}px 0 0`,
    },
    '&:last-of-type': {
      borderRadius: `0 0 ${theme.borderRadius[8]}px ${theme.borderRadius[8]}px`,
    },
  };
}, getAllStyledProps);
